<template>
  <v-app>
    <v-main class="login-page">
      <v-container fluid fill-height class="pa-0">
        <v-row no-gutters class="fill-height">
          <!-- Left side - Login form -->
          <v-col cols="12" lg="5" xl="4" class="login-form-container">
            <v-card elevation="0" class="login-card fill-height d-flex flex-column justify-center" color="transparent">
              <!-- Server menu -->
              <v-menu offset-y right transition="slide-y-transition" class="server-menu">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" class="server-menu-btn" color="primary">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="changeServerAddress">
                    <v-list-item-icon>
                      <v-icon>mdi-server</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ $t('login.change_server') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <div class="login-content mx-auto">
                <!-- Logo -->
                <div class="text-center mb-8 logo-container">
                  <v-img
                    :src="require('@/assets/logo/icon-geebung.png')"
                    alt="Salamanda ERP"
                    contain
                    max-height="120"
                    class="mx-auto logo-animation"
                  ></v-img>
                </div>

                <!-- Title -->
                <h1 class="text-h4 font-weight-bold text-center mb-2 primary--text">{{ $t('login.title') }}</h1>
                <p class="text-body-1 text-center mb-8 text--secondary">{{ $t('login.system_description') }}</p>

                <!-- Login Form -->
                <v-form @submit.prevent="login" class="login-form">
                  <v-card-text class="pa-0">
                    <!-- Username field -->
                    <v-text-field
                      v-model="data.username"
                      :label="$t('login.username')"
                      type="email"
                      outlined
                      rounded
                      :error-messages="auth_error ? auth_message : ''"
                      @keyup.enter="focusPass()"
                      ref="username"
                      prepend-inner-icon="mdi-account"
                      required
                      autocomplete="username"
                      class="mb-4 form-field"
                      color="primary"
                    ></v-text-field>

                    <!-- Password field -->
                    <v-text-field
                      v-model="data.password"
                      :label="$t('login.password')"
                      type="password"
                      outlined
                      rounded
                      @keyup.enter="login"
                      ref="password"
                      prepend-inner-icon="mdi-lock"
                      required
                      autocomplete="current-password"
                      class="mb-6 form-field"
                      color="primary"
                    ></v-text-field>

                    <!-- Login button -->
                    <v-btn
                      color="primary"
                      x-large
                      block
                      rounded
                      elevation="4"
                      height="56"
                      :loading="loading"
                      type="submit"
                      class="login-btn text-none font-weight-bold"
                      :disabled="!data.username || !data.password"
                    >
                      <v-icon left>mdi-login</v-icon>
                      {{ $t('login.button') }}
                    </v-btn>
                  </v-card-text>
                </v-form>

                <!-- Footer -->
                <div class="text-center mt-8">
                  <p class="text-caption text--secondary">
                    {{ $t('login.copyright') }}
                  </p>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Right side - Feature Slider -->
          <v-col cols="12" lg="7" xl="8" class="feature-slider-container d-none d-lg-flex">
            <div class="feature-slider fill-height">
              <!-- Slider Navigation -->
              <div class="slider-nav">
                <v-btn
                  v-for="(slide, index) in slides"
                  :key="index"
                  icon
                  small
                  :color="currentSlide === index ? 'white' : 'rgba(255,255,255,0.5)'"
                  @click="goToSlide(index)"
                  class="mx-1"
                >
                  <v-icon>mdi-circle</v-icon>
                </v-btn>
              </div>

              <!-- Slides -->
              <div class="slides-container">
                <transition-group name="slide" tag="div" class="slides-wrapper">
                  <div
                    v-for="(slide, index) in slides"
                    :key="slide.id"
                    v-show="currentSlide === index"
                    class="slide-content"
                  >
                    <!-- Background Pattern -->
                    <div class="slide-background" :class="slide.bgClass"></div>

                    <!-- Content -->
                    <div class="slide-text-content">
                      <div class="slide-icon-container mb-6">
                        <v-icon size="80" color="white" class="slide-icon">{{ slide.icon }}</v-icon>
                      </div>

                      <h2 class="text-h3 font-weight-bold white--text mb-4 slide-title">
                        {{ slide.title }}
                      </h2>

                      <p class="text-h6 white--text text-center mx-auto slide-description">
                        {{ slide.description }}
                      </p>

                      <!-- Feature Highlights -->
                      <div class="feature-highlights mt-8">
                        <div
                          v-for="(feature, fIndex) in slide.features"
                          :key="fIndex"
                          class="feature-item"
                        >
                          <v-icon color="white" class="mr-3">mdi-check-circle</v-icon>
                          <span class="white--text">{{ feature }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Animated Elements -->
                    <div class="slide-animations">
                      <div
                        v-for="(animation, aIndex) in slide.animations"
                        :key="aIndex"
                        class="animated-element"
                        :class="animation.class"
                        :style="animation.style"
                      ></div>
                    </div>
                  </div>
                </transition-group>
              </div>

              <!-- Auto-play indicator -->
              <div class="auto-play-indicator">
                <v-icon color="white" small>mdi-play-circle</v-icon>
                <span class="white--text ml-2">{{ $t('login.auto_play') }}</span>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import $ from 'jquery'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'Login',
  data () {
    return {
      data: {
        username: '',
        password: ''
      },
      auth_message: '',
      currentSlide: 0,
      slideInterval: null,
      slides: [
        {
          id: 'inventory',
          title: this.$t('login.features.inventory.title'),
          description: this.$t('login.features.inventory.description'),
          icon: 'mdi-package-variant-closed',
          bgClass: 'bg-inventory',
          features: [
            this.$t('login.features.inventory.feature1'),
            this.$t('login.features.inventory.feature2'),
            this.$t('login.features.inventory.feature3'),
            this.$t('login.features.inventory.feature4')
          ],
          animations: [
            { class: 'floating-box', style: { top: '20%', left: '10%', animationDelay: '0s' } },
            { class: 'floating-box', style: { top: '60%', right: '15%', animationDelay: '1s' } },
            { class: 'pulse-circle', style: { top: '40%', left: '80%', animationDelay: '2s' } }
          ]
        },
        {
          id: 'pos',
          title: this.$t('login.features.pos.title'),
          description: this.$t('login.features.pos.description'),
          icon: 'mdi-cash-register',
          bgClass: 'bg-pos',
          features: [
            this.$t('login.features.pos.feature1'),
            this.$t('login.features.pos.feature2'),
            this.$t('login.features.pos.feature3'),
            this.$t('login.features.pos.feature4')
          ],
          animations: [
            { class: 'cash-flow', style: { top: '30%', left: '20%', animationDelay: '0s' } },
            { class: 'receipt-paper', style: { bottom: '20%', right: '20%', animationDelay: '1.5s' } },
            { class: 'scan-line', style: { top: '50%', left: '50%', animationDelay: '0.5s' } }
          ]
        },
        {
          id: 'accounting',
          title: this.$t('login.features.accounting.title'),
          description: this.$t('login.features.accounting.description'),
          icon: 'mdi-chart-line',
          bgClass: 'bg-accounting',
          features: [
            this.$t('login.features.accounting.feature1'),
            this.$t('login.features.accounting.feature2'),
            this.$t('login.features.accounting.feature3'),
            this.$t('login.features.accounting.feature4')
          ],
          animations: [
            { class: 'chart-line', style: { top: '25%', left: '15%', animationDelay: '0s' } },
            { class: 'chart-bar', style: { bottom: '30%', right: '25%', animationDelay: '0.8s' } },
            { class: 'pie-chart', style: { top: '60%', left: '70%', animationDelay: '1.2s' } }
          ]
        },
        {
          id: 'multilingual',
          title: this.$t('login.features.multilingual.title'),
          description: this.$t('login.features.multilingual.description'),
          icon: 'mdi-translate',
          bgClass: 'bg-multilingual',
          features: [
            this.$t('login.features.multilingual.feature1'),
            this.$t('login.features.multilingual.feature2'),
            this.$t('login.features.multilingual.feature3'),
            this.$t('login.features.multilingual.feature4')
          ],
          animations: [
            { class: 'language-bubble', style: { top: '20%', left: '20%', animationDelay: '0s' } },
            { class: 'language-bubble', style: { top: '40%', right: '30%', animationDelay: '0.6s' } },
            { class: 'language-bubble', style: { bottom: '30%', left: '60%', animationDelay: '1.2s' } }
          ]
        },
        {
          id: 'analytics',
          title: this.$t('login.features.analytics.title'),
          description: this.$t('login.features.analytics.description'),
          icon: 'mdi-chart-box',
          bgClass: 'bg-analytics',
          features: [
            this.$t('login.features.analytics.feature1'),
            this.$t('login.features.analytics.feature2'),
            this.$t('login.features.analytics.feature3'),
            this.$t('login.features.analytics.feature4')
          ],
          animations: [
            { class: 'data-point', style: { top: '25%', left: '25%', animationDelay: '0s' } },
            { class: 'data-point', style: { top: '45%', right: '35%', animationDelay: '0.4s' } },
            { class: 'data-point', style: { bottom: '35%', left: '50%', animationDelay: '0.8s' } }
          ]
        }
      ]
    }
  },
  computed: {
    ...mapState({
      loading: state => state.Auth.status === 'loading',
      auth_error: state => state.Auth.status === 'error',
      name: state => state.Auth.user.name,
      last_login: state => state.Auth.user.last_login,
      auth_status: state => state.Auth.status,
      intended: state => state.Auth.intended
    })
  },
  watch: {
    auth_status (value) {
      if (value === 'error') {
        this.auth_message = this.$t('login.error.message')
      }
    }
  },
  methods: {
    ...mapActions('Base', ['setBaseURL']),
    ...mapActions('Auth', ['setIntended']),
    login () {
      this.$authService.login(Object.assign({}, this.data))
        .then(res => {
          this.setLanguage(res.user.lang || 'en')
          this.$notify({
            title: this.$t('status.success'),
            message: this.$t('login.welcome', [this.name, this.last_login]),
            type: 'success'
          })
          if (this.intended) {
            this.$router.push({
              name: this.intended
            })
          } else {
            this.$router.push({
              name: 'dashboard'
            })
          }
        })
        .catch(err => {
          console.log(err, 'Error is here...')
          if (err) {
            const status = err.response
            if (err.response) {
              if (status === 500) {
                this.auth_message = this.$t('login.error.server')
              } else if (status === 401) {
                this.auth_message = err.response.data.error
              }
            } else if (err.request) {
              this.auth_message = err.request.stack
            }
          }
        })
    },
    focusPass () {
      this.$refs.password.focus()
    },
    changeServerAddress () {
      this.setBaseURL(null)
      this.setIntended(null)
      this.$router.push({ name: 'base' })
    },
    goToSlide (index) {
      this.currentSlide = index
      this.resetAutoPlay()
    },
    nextSlide () {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length
    },
    startAutoPlay () {
      this.slideInterval = setInterval(() => {
        this.nextSlide()
      }, 5000)
    },
    resetAutoPlay () {
      if (this.slideInterval) {
        clearInterval(this.slideInterval)
        this.startAutoPlay()
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.$refs.username.focus()
    })
    $('body').attr('class', 'login-page-body')
    this.startAutoPlay()
  },
  beforeDestroy () {
    if (this.slideInterval) {
      clearInterval(this.slideInterval)
    }
  },
  destroyed () {
    $('body').removeClass('login-page-body')
  }
}
</script>

<style scoped>
.login-page {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
}

.login-form-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.login-content {
  width: 100%;
  max-width: 420px;
  padding: 0 32px;
}

.server-menu {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 1;
}

.logo-container {
  animation: fadeInDown 1s ease-out;
}

.logo-animation {
  transition: transform 0.3s ease;
}

.logo-animation:hover {
  transform: scale(1.05);
}

.form-field {
  transition: all 0.3s ease;
}

.form-field:focus-within {
  transform: translateY(-2px);
}

.login-btn {
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  background: linear-gradient(45deg, #1976D2, #42A5F5);
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.3);
}

/* Feature Slider Styles */
.feature-slider-container {
  position: relative;
  overflow: hidden;
  height: 100vh;
  width: 100%;
}

.feature-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.slider-nav {
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  gap: 8px;
}

.slides-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slides-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.slide-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.slide-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  opacity: 0.8;
  transition: all 0.5s ease;
}

.bg-inventory {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-pos {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.bg-accounting {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.bg-multilingual {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.bg-analytics {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.slide-text-content {
  position: relative;
  z-index: 10;
  text-align: center;
  max-width: 600px;
}

.slide-icon-container {
  animation: bounceIn 1s ease-out;
}

.slide-title {
  animation: slideInUp 1s ease-out 0.2s both;
}

.slide-description {
  animation: slideInUp 1s ease-out 0.4s both;
}

.feature-highlights {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideInUp 1s ease-out 0.6s both;
}

.feature-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.slide-animations {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.animated-element {
  position: absolute;
  opacity: 0.6;
}

/* Animation Elements */
.floating-box {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  animation: float 3s ease-in-out infinite;
}

.pulse-circle {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.cash-flow {
  width: 80px;
  height: 20px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: cashFlow 2s ease-in-out infinite;
}

.receipt-paper {
  width: 4px;
  height: 100px;
  background: rgba(255, 255, 255, 0.6);
  animation: receiptPrint 3s ease-in-out infinite;
}

.scan-line {
  width: 100px;
  height: 2px;
  background: rgba(255, 255, 255, 0.8);
  animation: scan 2s ease-in-out infinite;
}

.chart-line {
  width: 120px;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);
  animation: chartLine 3s ease-in-out infinite;
}

.chart-bar {
  width: 20px;
  height: 80px;
  background: rgba(255, 255, 255, 0.4);
  animation: chartBar 2s ease-in-out infinite;
}

.pie-chart {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: transparent;
  animation: pieChart 4s linear infinite;
}

.language-bubble {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  animation: languageBubble 3s ease-in-out infinite;
}

.data-point {
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: dataPoint 2s ease-in-out infinite;
}

.auto-play-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  font-size: 12px;
  opacity: 0.7;
}

/* Slide Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.8s ease;
}

.slide-enter {
  opacity: 0;
  transform: translateX(100%);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

/* Keyframe Animations */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}

@keyframes cashFlow {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

@keyframes receiptPrint {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100%);
    opacity: 0;
  }
}

@keyframes scan {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes chartLine {
  0% {
    transform: scaleX(0);
  }
  50% {
    transform: scaleX(1);
  }
  100% {
    transform: scaleX(0);
  }
}

@keyframes chartBar {
  0%, 100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}

@keyframes pieChart {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes languageBubble {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.1) rotate(180deg);
  }
}

@keyframes dataPoint {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.5);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 1264px) {
  .login-content {
    max-width: 380px;
    padding: 0 24px;
  }
}

@media (max-width: 960px) {
  .login-content {
    padding: 0 20px;
    max-width: 340px;
  }

  .login-form-container {
    padding: 24px 0;
  }

  h1.text-h4 {
    font-size: 1.5rem !important;
  }

  .feature-slider-container {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .login-content {
    padding: 0 16px;
    max-width: 300px;
  }

  .server-menu {
    top: 16px;
    right: 16px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .login-form-container {
    background: rgba(30, 30, 30, 0.95);
  }
}
</style>
